import { useLayoutEffect } from 'react';

import { useGSAP } from '@/hooks/useGSAP';

interface TextRevealOptions {
  enabled?: boolean;
  trigger?: HTMLElement | null;
  yStart?: number;
  yEnd?: number;
  stagger?: number;
  delay?: number;
  duration?: number;
}

export const useTextReveal = (
  target: HTMLElement | null,
  {
    enabled = true,
    trigger = null,
    yStart = 100,
    yEnd = 0,
    stagger = 0.1,
    delay = 0,
    duration = 1,
  }: TextRevealOptions = {}
) => {
  const { gsap, ready } = useGSAP();

  useLayoutEffect(() => {
    if (!ready || !gsap || !enabled || !target) return;

    const spans = target.querySelectorAll('span');
    if (spans.length === 0) return;

    // Progressive enhancement: mark container as GSAP-ready before hiding
    target.classList.add('gsapReady');

    // Set initial state before paint
    gsap.set(spans, { y: yStart, opacity: 0 });

    const animation = gsap.to(spans, {
      y: yEnd,
      duration,
      stagger,
      opacity: 1,
      delay,
      ease: 'power3.out',
      scrollTrigger: trigger
        ? {
            trigger,
            start: 'top 100%',
            toggleActions: 'play none none reverse',
          }
        : undefined,
    });

    return () => {
      animation.kill();
      // Restore visibility on cleanup (progressive enhancement fallback)
      target.classList.remove('gsapReady');
      gsap.set(spans, { clearProps: 'opacity,y,transform' });
    };
  }, [
    target,
    enabled,
    trigger,
    delay,
    yStart,
    yEnd,
    stagger,
    duration,
    ready,
    gsap,
  ]);
};
